import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Zap, Target, TrendingUp, BookOpen, Shield, DollarSign, Users, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FAQ: React.FC = () => {
  const faqs = [
    {
      category: 'Getting Started',
      icon: Zap,
      items: [
        {
          q: 'What is the 30-Day Cash Sprint?',
          a: 'A focused program to take your idea from 0 → first paying customer in 30 days using daily accountability, AI validation, and action playbooks.'
        },
        {
          q: 'Do I need a business idea to start?',
          a: 'No. You can start with idea generation prompts and templates, then validate promising ones using the AI validation flow.'
        },
        {
          q: 'How much time do I need per day?',
          a: '1–2 hours is ideal. Consistency matters more than duration. The Boss Mode planner adapts to your availability.'
        }
      ]
    },
    {
      category: 'AI Assessment',
      icon: Target,
      items: [
        {
          q: 'How does assessment work?',
          a: 'You answer targeted questions. Our AI analyzes market, distribution, moat, founder–market fit, and risks, then provides a score and action list.'
        },
        {
          q: 'Which model do you use?',
          a: 'We are using the open AI api key for the assessment.'
        },
        {
          q: 'Can I run multiple validations?',
          a: 'Yes. With a premium plan you can compare multiple ideas and proceed with the best signal.'
        }
      ]
    },
    {
      category: 'Boss Mode & Accountability',
      icon: TrendingUp,
      items: [
        {
          q: 'What does Cashpurr do?',
          a: 'Cashpurr turns your sprint into a daily checklist, nudges you, tracks streaks, and keeps momentum with supportive-but-firm coaching.'
        },
        {
          q: 'How are streak days calculated?',
          a: 'Based on onboarding date and daily activity, not duplicate tasks. This avoids inflated counts.'
        },
        {
          q: 'Can I change reminders?',
          a: 'Yes. Adjust frequency and tone from gentle to strict in Settings.'
        }
      ]
    },
    {
      category: 'Billing & Plans',
      icon: DollarSign,
      items: [
        {
          q: 'Is there a free plan?',
          a: 'Yes. You can explore core features. Upgrade for advanced analytics, pod matching, and priority support.'
        },
        {
          q: 'Can I cancel anytime?',
          a: 'Yes. You keep access until the end of the billing cycle.'
        },
        {
          q: 'Refund policy?',
          a: '7-day money-back guarantee if you are not satisfied.'
        }
      ]
    },
    {
      category: 'Community Pods',
      icon: Users,
      items: [
        {
          q: 'How do pods work?',
          a: 'Small groups of founders matched by stage and goals for weekly check-ins and async chat.'
        },
        {
          q: 'Do I need to be on a call?',
          a: 'No. Async-first via in-app chat; optional live sessions.'
        }
      ]
    },
    {
      category: 'Support',
      icon: MessageCircle,
      items: [
        {
          q: 'How do I contact support?',
          a: 'Email hello@startupstreak.com. Average response: within 4 hours.'
        },
        {
          q: 'Do you have documentation?',
          a: 'Yes. See Docs for guides and tutorials. The FAQ covers the most common questions.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-teal-500 to-orange-400 text-white pt-32 pb-24">
        <div className="absolute inset-0 bg-dot-white/10" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-6xl md:text-7xl font-light tracking-tight mb-6 leading-tight">FAQ</h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed max-w-2xl mx-auto font-light">
              Answers to the most common questions about StartupStreak
            </p>
            <div className="mt-8">
              <a href="/help">
                <Button variant="outline" className="border-2 text-black bg-white">
                  Go to Help Center
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-24 bg-white text-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {faqs.map((group, idx) => (
              <div key={idx} className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 border border-gray-200 rounded-full flex items-center justify-center">
                    <group.icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-medium">{group.category}</h2>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg">
                  <Accordion type="single" collapsible className="w-full">
                    {group.items.map((item, i) => (
                      <AccordionItem key={i} value={`${idx}-${i}`}>
                        <AccordionTrigger className="text-left px-6">{item.q}</AccordionTrigger>
                        <AccordionContent className="px-6 text-gray-700 leading-relaxed">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-white/5" />
        <div className="container mx-auto px-4 relative">
          <div className="text-center max-w-3xl mx-auto">
            <h3 className="text-4xl md:text-5xl font-light mb-6">Still have questions?</h3>
            <p className="text-blue-200 mb-8">Email us and we’ll get back within a few hours.</p>
            <a href="mailto:hello@startupstreak.com">
              <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-semibold">
                Email Support
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
